// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-10-01 00:20:03 +0200 (Wed, 01 Oct 2025) $
//  Author    : Tobias Schwarzkopf, Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156785 $
//  Purpose   : setup redux store
// =============================================================================
// ---Third-party imports
import {createStore, applyMiddleware, compose} from 'redux'
import {default as createSagaMiddleware, END} from 'redux-saga'
//~ import createSagaMonitor from "@clarketm/saga-monitor";
import {fork, spawn} from 'redux-saga/effects'
import {initializeCurrentLocation, routerForBrowser, routerForExpress} from './redux_router'
import {persistStore} from 'redux-persist'

// ---Framework imports
import {buildReducers} from './reducers'
import {log, createServerLogger, createBrowserDevLogger, createBrowserProdLogger} from './logging'
import {routes, middleware as routingMiddleware} from './routing'
import {Actions} from './registries'
import {setupHotStore} from './hmr'
import {reportError} from 'uix/utils/error'

// ---Globals
export let reportSagaError = (error, sagaStack, store) => {
  reportError('SagaError', store, error, {sagaStack})
  store.dispatch(Actions.registerSagaError({code: 'SAGA_ERROR', message: error.message}))
}

export let reportReducerError = (error, store) => {
  reportError('ReducerError', store, error)
}

const catchReducerErrors = store => {
  const next = store.dispatch
  store.dispatch = action => {
    try {
      return next(action)
    } catch (error) {
      reportReducerError(error, store)
      console.error(`Error during reducer call on ${action.type}\n`, error)
      //~ throw error
    }
  }
}

let configureBrowserStore = rootSaga => {
  // https://github.com/FormidableLabs/redux-little-router#wiring-up-the-boilerplate
  // Install the router into the store for a browser-only environment.
  // routerForBrowser is a factory method that returns a store
  // enhancer and a middleware.
  let router = routerForBrowser({routes})

  // router reducer needs to be called "router"!
  let reducers = buildReducers({router: router.reducer})

  let onSagaError = (error, {sagaStack}) => {
    /* fatal root saga error, all sagas are down now
    dispatch a final action
    */
    reportSagaError(error, sagaStack, store)
    console.error(error)
    console.error(sagaStack)
  }
  // Store stuff
  let sagaMiddleware = createSagaMiddleware({onError: onSagaError})
  //~ let sagaMiddleware = createSagaMiddleware()

  let middlewares = [router.middleware, routingMiddleware, sagaMiddleware]
  // sagaMiddleware should be last middleware: https://github.com/redux-saga/redux-saga/tree/master/docs/api#selectselector-args
  if (process.env.NODE_ENV !== 'production') {
    // immer based reducers would auto freeze
    // if you add mutable stuff (dom-nodes) or mutate stuff in store you get a: TypeError: Cannot freeze
    const freeze = require('redux-freeze')
    middlewares = [createBrowserDevLogger(), ...middlewares, freeze]
  } else {
    middlewares = [createBrowserProdLogger(), ...middlewares]
  }

  let enhancers = compose(
    router.enhancer,
    applyMiddleware(...middlewares)
  )

  let store = createStore(reducers, enhancers)
  catchReducerErrors(store)

  let persistor = persistStore(store)

  // setup hot module reloading (HMR)
  setupHotStore(store, reducers)

  sagaMiddleware.run(rootSaga)

  // trigger first ROUTER_LOCATION_CHANGED in browser
  let initialLocation = store.getState().router
  if (initialLocation) {
    store.dispatch(initializeCurrentLocation(initialLocation))
  }
  return {store, persistor}
}

let configureExpressStore = (rootSaga, req, apiurl, preloadedState, options, testServerSaga) => {
  let router = routerForExpress({routes, request: req})
  let reducers = buildReducers({router: router.reducer})

  let onSagaError = (error, {sagaStack}) => {
    /* fatal root saga error, all sagas are down now
    dispatch a final action
    */
    reportSagaError(error, sagaStack, store)
    //~ console.error(error)
    //~ console.error(sagaStack)

    // see server.js/onSagaError
    // nah - testReducerError hangs - so it's probably not a good idea to throw here
    //~ throw error
  }

  let monconfig = {
    level: 'info',
    verbose: true,
    effectTrigger: true,
    effectResolve: false,
    actionDispatch: true,
  }

  let sagaMiddleware = createSagaMiddleware({
    onError: onSagaError,
    //~ sagaMonitor: createSagaMonitor(monconfig)
  })
  let middlewares = [createServerLogger(), router.middleware, routingMiddleware, sagaMiddleware]

  // this makes reducing really slow if store size grows
  if (process.env.NODE_ENV !== 'production' && options.reduxFreeze) {
    const freeze = require('redux-freeze')
    middlewares = [...middlewares, freeze]
  }

  let enhancers = compose(
    router.enhancer,
    applyMiddleware(...middlewares)
  )

  const store = createStore(reducers, preloadedState, enhancers)
  catchReducerErrors(store)

  store.runSaga = sagaMiddleware.run
  store.endSagas = () => {
    log.saga('END')
    store.dispatch(END)
  }

  let running = sagaMiddleware.run(function*() {
    yield fork(rootSaga)
    yield fork(testServerSaga)
  })

  if (!preloadedState) {
    let initialLocation = store.getState().router
    if (initialLocation) {
      store.dispatch(initializeCurrentLocation(initialLocation))
      store.dispatch(Actions.setAppState({state: 'INIT_APP', apiurl}))
    }
  }

  return {store, sagas: running}
}

export {configureBrowserStore, configureExpressStore}
